import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import PropertyCard from '@/components/PropertyCard';
import VerifiedBadge from '@/components/VerifiedBadge';
import TrustScore from '@/components/TrustScore';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, User, Mail, Phone, MapPin, Lock, Crown, Building2, Globe2, Languages, BarChart3 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AgentProfilePage() {
  const { id } = useParams();
  const { language } = useLanguage();
  const { token, isAuthenticated } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const tt = (en, es) => (language === 'es' ? es : en);

  useEffect(() => {
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    axios
      .get(`${API}/agents/${id}/profile`, { headers })
      .then((res) => setData(res.data))
      .catch(() => toast.error(tt('Agent not found', 'Agente no encontrado')))
      .finally(() => setLoading(false));
  }, [id, token]);

  if (loading)
    return (
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="agent-profile-loading">
        <Skeleton className="h-8 w-48 mb-6" />
        <Skeleton className="h-40 rounded-2xl mb-6" />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => <Skeleton key={i} className="h-64 rounded-xl" />)}
        </div>
      </div>
    );

  if (!data) return <div className="text-center py-20 text-slate-500">{tt('Agent not found', 'Agente no encontrado')}</div>;

  const { agent, verified, agency, properties, stats, contact_visible, trust_score } = data;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="agent-profile-page">
      <Link to="/search" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-primary mb-4 transition-colors" data-testid="back-link">
        <ArrowLeft className="w-4 h-4" />
        {tt('Back to search', 'Volver a búsqueda')}
      </Link>

      {/* Header card */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#0B192C] to-[#1B2A4E] text-white p-6 md:p-8 mb-8" data-testid="agent-header">
        <div className="absolute top-0 right-0 w-64 h-64 bg-[#D4AF37]/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
        <div className="relative flex flex-col md:flex-row gap-6 items-start md:items-center">
          <div className="w-24 h-24 md:w-28 md:h-28 rounded-full bg-[#D4AF37]/20 border-2 border-[#D4AF37] flex items-center justify-center overflow-hidden flex-shrink-0">
            {agent.photo ? (
              <img src={agent.photo} alt={agent.name} className="w-full h-full object-cover" />
            ) : (
              <User className="w-12 h-12 text-[#D4AF37]" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <h1 className="font-heading text-2xl md:text-3xl font-bold" data-testid="agent-name">{agent.name}</h1>
              {verified && <VerifiedBadge role={agent.role} size="sm" />}
              {agent.agent_tier === 'pro' && (
                <Badge className="bg-[#D4AF37] text-[#0B192C] gap-1"><Crown className="w-3 h-3" />PRO</Badge>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-3 text-sm text-slate-300">
              <span className="capitalize">{agent.role === 'agent' ? tt('Real Estate Agent', 'Agente Inmobiliario') : agent.role}</span>
              {(agent.city_suburb || agent.country) && (
                <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{[agent.city_suburb, agent.country].filter(Boolean).join(', ')}</span>
              )}
            </div>
            {agent.bio && <p className="mt-3 text-sm text-slate-200 leading-relaxed max-w-2xl" data-testid="agent-bio">{agent.bio}</p>}
            {agent.languages_spoken?.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1.5">
                <Languages className="w-4 h-4 text-[#D4AF37]" />
                {agent.languages_spoken.map((l) => (
                  <Badge key={l} variant="outline" className="border-[#D4AF37]/40 text-[#D4AF37] text-[10px]">{l}</Badge>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mb-8">
        {/* Stats + Trust Score */}
        <div className="lg:col-span-2 grid grid-cols-2 gap-4">
          {trust_score && (
            <div className="col-span-2 bg-white rounded-xl border border-border p-5" data-testid="agent-trust-card">
              <TrustScore data={trust_score} variant="large" testIdPrefix="agent-trust" />
            </div>
          )}
          <div className="bg-white rounded-xl border border-border p-5" data-testid="stat-listings">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500 mb-1">
              <Building2 className="w-3.5 h-3.5" />{tt('Active Listings', 'Propiedades Activas')}
            </div>
            <div className="font-heading text-3xl font-bold text-primary">{stats.active_listings}</div>
          </div>
          <div className="bg-white rounded-xl border border-border p-5" data-testid="stat-completed">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500 mb-1">
              <BarChart3 className="w-3.5 h-3.5" />{tt('Completed Sales', 'Ventas Completadas')}
            </div>
            <div className="font-heading text-3xl font-bold text-[#D4AF37]">{stats.completed_transactions}</div>
          </div>
          {agency && (
            <div className="col-span-2 bg-white rounded-xl border border-border p-5" data-testid="agent-agency">
              <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500 mb-1">
                <Globe2 className="w-3.5 h-3.5" />{tt('Agency', 'Agencia')}
              </div>
              <div className="font-heading text-lg font-semibold text-slate-900">{agency.name}</div>
              {agency.description && <p className="text-sm text-slate-500 mt-1 line-clamp-2">{agency.description}</p>}
            </div>
          )}
        </div>

        {/* Contact Card */}
        <div className="bg-white rounded-xl border border-border p-5" data-testid="agent-contact-card">
          <h3 className="font-heading font-semibold mb-3">{tt('Contact', 'Contacto')}</h3>
          {contact_visible ? (
            <div className="space-y-2 text-sm">
              {agent.email && (
                <div className="flex items-center gap-2 text-slate-700" data-testid="agent-email">
                  <Mail className="w-3.5 h-3.5 text-primary" />
                  <a href={`mailto:${agent.email}`} className="hover:text-primary truncate">{agent.email}</a>
                </div>
              )}
              {agent.phone && (
                <div className="flex items-center gap-2 text-slate-700" data-testid="agent-phone">
                  <Phone className="w-3.5 h-3.5 text-primary" />
                  <a href={`tel:${agent.phone}`} className="hover:text-primary">{agent.phone}</a>
                </div>
              )}
              {!agent.email && !agent.phone && (
                <p className="text-xs text-slate-500">{tt('No public contact info', 'Sin información de contacto pública')}</p>
              )}
            </div>
          ) : (
            <div className="bg-gradient-to-br from-[#0B192C]/5 to-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-lg p-3" data-testid="agent-contact-locked">
              <div className="flex items-center gap-2 text-sm font-semibold text-[#0B192C] mb-1">
                <Lock className="w-3.5 h-3.5 text-[#D4AF37]" />
                {tt('Premium Contact', 'Contacto Premium')}
              </div>
              <p className="text-xs text-slate-600 mb-2">
                {tt(
                  'Upgrade to Premium Buyer to view this agent\'s email and phone.',
                  'Mejora a Comprador Premium para ver el correo y teléfono de este agente.'
                )}
              </p>
              <Link to={isAuthenticated ? '/upgrade' : '/auth'}>
                <Button size="sm" className="w-full bg-[#D4AF37] hover:bg-[#C8A028] text-[#0B192C] gap-1.5 h-8" data-testid="agent-upgrade-btn">
                  <Crown className="w-3.5 h-3.5" />
                  {tt('Upgrade to Premium', 'Mejorar a Premium')}
                </Button>
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* Listings */}
      <div>
        <h2 className="font-heading text-xl md:text-2xl font-bold text-slate-900 mb-4" data-testid="agent-listings-heading">
          {tt(`Listings by ${agent.name}`, `Propiedades de ${agent.name}`)}
        </h2>
        {properties.length === 0 ? (
          <div className="text-center py-16 bg-slate-50 rounded-xl text-slate-500" data-testid="no-listings">
            {tt('No active listings yet.', 'Sin propiedades activas aún.')}
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="agent-listings-grid">
            {properties.map((p, i) => (
              <PropertyCard key={p.id} property={p} index={i} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
